/* global Word */
import React, { useCallback, useMemo, useReducer } from "react";
import { DefaultButton, PrimaryButton } from "@fluentui/react/lib/Button";
import { Spinner, SpinnerSize } from "@fluentui/react/lib/Spinner";
import { MessageBar, MessageBarType } from "@fluentui/react/lib/MessageBar";
import { DetailsList, DetailsListLayoutMode, IColumn, SelectionMode } from "@fluentui/react/lib/DetailsList";
import { Separator } from "@fluentui/react/lib/Separator";
import { saveAs } from "file-saver";

import { TroubleshootingToolProps } from "../../troubleshooting-tools.types";
import { DocPropKeys, retrieveDocumentProp } from "../../../../../utils/helpers/CustomXmlHelper";
import { AnalysisState, CustomXmlData, DocumentPropertiesData } from "./document-analyzer.types";
import { formatXml, getXmlContentByNamespace } from "./document-analyzer.helper";
import {
  formatMessage,
  formatMessageDefault,
  formatMessageWithParams,
  formatMessageWithParamsDefault,
} from "../../../../../utils/helpers/LanguageHelper";
import { getDocumentCoObjects } from "../../../../../utils/helpers/ContentControlHelper";
import { CoObjectItem } from "../../../../../components/interfaces/CoObject";

const NOT_PRESENT = formatMessageDefault("cof.troubleshooting.documentanalyzer.notPresent", "not present");

const fetchDocumentProperties = async (): Promise<DocumentPropertiesData> => {
  try {
    const [documentId, tenantId] = await Promise.all([
      retrieveDocumentProp(DocPropKeys.DocumentId),
      retrieveDocumentProp(DocPropKeys.TenantId),
    ]);

    return {
      documentId: documentId || NOT_PRESENT,
      tenantId: tenantId || NOT_PRESENT,
    };
  } catch (error) {
    console.error("Error fetching document properties:", error);
    return { documentId: NOT_PRESENT, tenantId: NOT_PRESENT };
  }
};

const fetchCustomXmlData = async (): Promise<CustomXmlData[]> => {
  try {
    const namespaces = await Word.run(async (context) => {
      const customXmlParts = context.document.customXmlParts;

      customXmlParts.load("items/namespaceUri");
      await context.sync();

      const coNamespaces = customXmlParts.items
        .map((part) => part.namespaceUri)
        .filter((ns): ns is string => Boolean(ns) && ns.startsWith("CustomXMLCo"))
        .sort((a, b) => a.localeCompare(b));

      return Array.from(new Set(coNamespaces));
    });

    return await Promise.all(
      namespaces.map(async (namespace) => {
        try {
          const xmlContent = await getXmlContentByNamespace(namespace);
          const formattedXml = xmlContent ? formatXml(xmlContent) : "";

          return { namespace, xmlContent: xmlContent || "", formattedXml };
        } catch (error) {
          console.error(`Error fetching XML for namespace ${namespace}:`, error);
          return { namespace, xmlContent: "", formattedXml: "" };
        }
      })
    );
  } catch (error) {
    console.error("Error fetching custom XML data:", error);
    return [];
  }
};

const fetchContentControls = async (): Promise<CoObjectItem[]> => {
  return getDocumentCoObjects();
};

type Action =
  | { type: "START" }
  | {
      type: "SUCCESS";
      payload: {
        contentControls: CoObjectItem[];
        documentProperties: DocumentPropertiesData;
        customXmlData: CustomXmlData[];
      };
    }
  | { type: "ERROR"; payload: string }
  | { type: "RESET" };

const initialState: AnalysisState = {
  isLoading: false,
  contentControls: null,
  documentProperties: null,
  customXmlData: null,
  error: null,
};

function initState(state: AnalysisState, action: Action): AnalysisState {
  switch (action.type) {
    case "START":
      return {
        isLoading: true,
        contentControls: null,
        documentProperties: null,
        customXmlData: null,
        error: null,
      };
    case "SUCCESS":
      return {
        isLoading: false,
        contentControls: action.payload.contentControls,
        documentProperties: action.payload.documentProperties,
        customXmlData: action.payload.customXmlData,
        error: null,
      };
    case "ERROR":
      return {
        isLoading: false,
        contentControls: null,
        documentProperties: null,
        customXmlData: null,
        error: action.payload,
      };
    case "RESET":
      return initialState;
    default:
      return state;
  }
}

function buildExportTxt(state: AnalysisState): string {
  const separator = "=".repeat(80);
  const lines: string[] = [];
  const nl = "\n";

  const pushSectionHeader = (title: string) => {
    lines.push(separator, nl, title, nl, separator, nl, nl);
  };

  pushSectionHeader(
    formatMessageDefault(
      "cof.troubleshooting.documentanalyzer.export.section.documentproperties",
      "DOCUMENT PROPERTIES"
    )
  );

  if (state.documentProperties) {
    lines.push(
      formatMessageWithParams("cof.troubleshooting.documentanalyzer.export.contentcontrol.id", {
        0: state.documentProperties.documentId,
      }),
      nl,
      formatMessageWithParams("cof.troubleshooting.documentanalyzer.export.contentcontrol.tenantid", {
        0: state.documentProperties.tenantId,
      }),
      nl
    );
  } else {
    lines.push(
      formatMessageDefault("cof.troubleshooting.documentanalyzer.export.nodocprops", "No document properties found."),
      nl
    );
  }

  lines.push(nl, nl);

  pushSectionHeader(
    `${formatMessageDefault(
      "cof.troubleshooting.documentanalyzer.export.section.customxml",
      "CUSTOM XML NAMESPACES"
    )} (${state.customXmlData?.length || 0})`
  );

  if (state.customXmlData?.length) {
    state.customXmlData.forEach((xmlData, index) => {
      lines.push(
        formatMessageWithParamsDefault("cof.troubleshooting.documentanalyzer.export.namespace.header", "Header", {
          0: index + 1,
          namespace: xmlData.namespace,
        }),
        nl,
        nl
      );

      lines.push(
        xmlData.formattedXml
          ? `${xmlData.formattedXml}${nl}${nl}`
          : `${formatMessageDefault(
              "cof.troubleshooting.documentanalyzer.export.noxmlcontent",
              "(No XML content)"
            )}${nl}${nl}`
      );

      if (index < state.customXmlData!.length - 1) {
        lines.push(nl);
      }
    });
  } else {
    lines.push(
      formatMessageDefault(
        "cof.troubleshooting.documentanalyzer.export.noxmlnamespaces",
        "No CustomXMLCo namespaces found."
      ),
      nl
    );
  }

  lines.push(nl, nl);

  pushSectionHeader(
    `${formatMessageDefault(
      "cof.troubleshooting.documentanalyzer.export.section.contentcontrols",
      "CONTENT CONTROLS"
    )} (${state.contentControls?.length || 0})`
  );

  if (state.contentControls?.length) {
    state.contentControls.forEach((cc, index) => {
      const formattedJsonTag = JSON.stringify(cc, null, 2);

      lines.push(
        formatMessageWithParamsDefault("cof.troubleshooting.documentanalyzer.export.contentcontrol.header", "Header", {
          0: index + 1,
        }),
        nl,
        formatMessageWithParamsDefault("cof.troubleshooting.documentanalyzer.export.contentcontrol.id", "ID", {
          0: cc.contentControlId ?? cc.id ?? "-",
        }),
        nl,
        formatMessageWithParamsDefault("cof.troubleshooting.documentanalyzer.export.contentcontrol.title", "Title", {
          0:
            cc.info?.objectName ||
            formatMessageDefault("cof.troubleshooting.documentanalyzer.export.tag.empty", "(empty)"),
        }),
        nl,
        formatMessageWithParamsDefault("cof.troubleshooting.documentanalyzer.export.contentcontrol.type", "Type", {
          0: cc.info?.objectType || "-",
        }),
        nl,
        formatMessageWithParamsDefault(
          "cof.troubleshooting.documentanalyzer.export.contentcontrol.parameters",
          "Parameters",
          {
            0: cc.parameters && cc.parameters.length > 0 ? formatMessage("cof.base.yes") : formatMessage("cof.base.no"),
          }
        ),
        nl,
        formatMessageWithParamsDefault("cof.troubleshooting.documentanalyzer.export.contentcontrol.locked", "Locked", {
          0: cc.isLocked ? formatMessage("cof.base.yes") : formatMessage("cof.base.no"),
        }),
        nl,
        formatMessageWithParamsDefault("cof.troubleshooting.documentanalyzer.export.contentcontrol.source", "Source:", {
          0: getSourceLabel(cc.source),
        }),
        nl,
        nl
      );

      if (cc.originalPayload) {
        lines.push(
          formatMessageDefault("cof.troubleshooting.documentanalyzer.export.contentcontrol.payload", "Payload:"),
          nl,
          cc.originalPayload,
          nl
        );
      } else {
        lines.push(
          formatMessageDefault(
            "cof.troubleshooting.documentanalyzer.export.contentcontrol.payload.empty",
            "Payload: (none)"
          ),
          nl
        );
      }

      lines.push(nl);

      lines.push(
        formatMessageDefault("cof.troubleshooting.documentanalyzer.export.contentcontrol.jsontag.label", "JSON Tag:"),
        nl,
        formattedJsonTag ||
          formatMessageDefault(
            "cof.troubleshooting.documentanalyzer.export.contentcontrol.jsontag.empty",
            "JSON Tag: (none)"
          ),
        nl,
        nl
      );

      if (index < state.contentControls!.length - 1) {
        lines.push(nl);
      }
    });
  } else {
    lines.push(
      formatMessageDefault(
        "cof.troubleshooting.documentanalyzer.export.nocontentcontrols",
        "No content controls found."
      ),
      nl
    );
  }

  lines.push(nl, nl, separator, nl);
  lines.push(
    formatMessageWithParamsDefault("cof.troubleshooting.documentanalyzer.export.generatedAt", "Generated at:", {
      0: new Date().toISOString(),
    }),
    nl,
    separator,
    nl
  );

  return lines.join("");
}

export const DocumentAnalyzer: React.FC<TroubleshootingToolProps> = ({ onError, onSuccess }) => {
  const [state, dispatch] = useReducer(initState, initialState);

  const hasResults =
    Boolean(state.contentControls) || Boolean(state.documentProperties) || Boolean(state.customXmlData);

  const analyzeContentControls = useCallback(async () => {
    dispatch({ type: "START" });

    try {
      const [documentProperties, customXmlData, contentControls] = await Promise.all([
        fetchDocumentProperties(),
        fetchCustomXmlData(),
        fetchContentControls(),
      ]);

      dispatch({
        type: "SUCCESS",
        payload: { documentProperties, customXmlData, contentControls },
      });

      onSuccess?.(
        formatMessageWithParams("cof.troubleshooting.documentanalyzer.onSuccess.analysis", {
          controls: contentControls.length,
          namespaces: customXmlData.length,
        })
      );

      console.log("Analysis completed:", { documentProperties, customXmlData, contentControls });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error occurred";
      dispatch({ type: "ERROR", payload: message });

      onError?.(error instanceof Error ? error : new Error(message));
    }
  }, [onError, onSuccess]);

  const exportToTxt = useCallback(() => {
    if (!hasResults) return;

    try {
      const txtContent = buildExportTxt(state);
      const blob = new Blob([txtContent], { type: "text/plain;charset=utf-8" });
      const timestamp = new Date().toISOString().replace(/[:.]/g, "-");

      saveAs(blob, `document-properties-${timestamp}.txt`);

      onSuccess?.(
        formatMessageDefault(
          "cof.troubleshooting.documentanalyzer.onSuccess.export",
          "Data exported successfully to TXT file"
        )
      );
    } catch (error) {
      onError?.(
        error instanceof Error
          ? error
          : new Error(formatMessageDefault("cof.troubleshooting.documentanalyzer.onError.export", "Export failed"))
      );
    }
  }, [hasResults, onError, onSuccess, state]);

  const contentControlColumns: IColumn[] = useMemo(
    () => [
      {
        key: "contentControlId",
        name: formatMessageDefault("cof.troubleshooting.documentanalyzer.column.id", "ID"),
        fieldName: "contentControlId",
        minWidth: 60,
        maxWidth: 80,
        isResizable: true,
        onRender: (item: CoObjectItem) => (
          <span style={{ fontFamily: "monospace" }}>{item.contentControlId ?? "-"}</span>
        ),
      },
      {
        key: "title",
        name: formatMessageDefault("cof.troubleshooting.documentanalyzer.column.title", "Title"),
        fieldName: "title",
        minWidth: 150,
        maxWidth: 250,
        isResizable: true,
        onRender: (item: CoObjectItem) => <span>{item.info?.objectName || "-"}</span>,
      },
      {
        key: "objectType",
        name: formatMessageDefault("cof.troubleshooting.documentanalyzer.coobject.column.type", "Type"),
        fieldName: "info",
        minWidth: 100,
        maxWidth: 140,
        isResizable: true,
        onRender: (item: CoObjectItem) => <span>{item.info?.objectType || "-"}</span>,
      },
      {
        key: "sourceType",
        name: formatMessageDefault("cof.troubleshooting.documentanalyzer.coobject.column.source", "Source"),
        fieldName: "source",
        minWidth: 120,
        maxWidth: 180,
        onRender: (item: CoObjectItem) => <span>{getSourceLabel(item.source)}</span>,
      },
      {
        key: "parameters",
        name: formatMessageDefault("cof.troubleshooting.documentanalyzer.coobject.column.parameters", "Parameters"),
        fieldName: "parameters",
        minWidth: 80,
        maxWidth: 100,
        onRender: (item: CoObjectItem) => {
          const count = item.parameters?.length ?? 0;
          const has = count > 0;
          return <span style={{ color: has ? "green" : "gray", fontWeight: has ? 600 : "normal" }}>{count}</span>;
        },
      },
      {
        key: "locked",
        name: formatMessageDefault("cof.troubleshooting.documentanalyzer.coobject.column.locked", "Locked"),
        fieldName: "isLocked",
        minWidth: 60,
        maxWidth: 80,
        onRender: (item: CoObjectItem) => <span>{item.isLocked ? "🔒" : ""}</span>,
      },
      {
        key: "payload",
        name: formatMessageDefault("cof.troubleshooting.documentanalyzer.coobject.column.payload", "Payload"),
        fieldName: "originalPayload",
        minWidth: 60,
        maxWidth: 80,
        onRender: (item: CoObjectItem) => (
          <span style={{ color: item.originalPayload ? "green" : "gray" }}>{item.originalPayload ? "✓" : "-"}</span>
        ),
      },
    ],
    []
  );

  const renderDocumentProperties = () => {
    if (!state.documentProperties) return null;

    const { documentId, tenantId } = state.documentProperties;

    return (
      <div style={{ marginBottom: "1.5rem" }}>
        <Separator alignContent="start">
          <div style={{ fontWeight: 600, fontSize: 16 }}>
            {formatMessageDefault(
              "cof.troubleshooting.documentanalyzer.documentProperties.title",
              "Document Properties"
            )}
          </div>
        </Separator>

        <div style={{ padding: "1rem", backgroundColor: "#f9f9f9", borderRadius: 4, marginTop: "0.5rem" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            <div>
              <strong>
                {formatMessageDefault(
                  "cof.troubleshooting.documentanalyzer.documentProperties.documentId",
                  "Document-ID"
                )}
                :
              </strong>{" "}
              <span
                style={{
                  color: documentId === NOT_PRESENT ? "red" : "green",
                  fontFamily: "monospace",
                }}
              >
                {documentId}
              </span>
            </div>

            <div>
              <strong>
                {formatMessageDefault("cof.troubleshooting.documentanalyzer.documentProperties.tenantId", "Tenant-ID")}:
              </strong>{" "}
              <span
                style={{
                  color: tenantId === NOT_PRESENT ? "red" : "green",
                  fontFamily: "monospace",
                }}
              >
                {tenantId}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderCustomXmlNamespaces = () => {
    if (!state.customXmlData) return null;

    return (
      <div style={{ marginBottom: "1.5rem" }}>
        <Separator alignContent="start">
          <div style={{ fontWeight: 600, fontSize: 16 }}>
            {formatMessageDefault("cof.troubleshooting.documentanalyzer.customXml.title", "Custom XML Namespaces")} (
            {state.customXmlData.length})
          </div>
        </Separator>

        {state.customXmlData.length === 0 ? (
          <MessageBar messageBarType={MessageBarType.info} styles={{ root: { marginTop: "0.5rem" } }}>
            {formatMessageDefault(
              "cof.troubleshooting.documentanalyzer.customXml.none",
              "No CustomXMLCo namespaces found in the document."
            )}
          </MessageBar>
        ) : (
          <div
            style={{
              padding: "1rem",
              backgroundColor: "#f9f9f9",
              borderRadius: 4,
              marginTop: "0.5rem",
              maxHeight: 200,
              overflowY: "auto",
            }}
          >
            <ul style={{ margin: 0, paddingLeft: "1.5rem" }}>
              {state.customXmlData.map((xmlData) => (
                <li
                  key={xmlData.namespace}
                  style={{
                    fontFamily: "monospace",
                    fontSize: 13,
                    marginBottom: "0.25rem",
                    wordBreak: "break-all",
                  }}
                >
                  {xmlData.namespace}
                  {xmlData.xmlContent && <span style={{ marginLeft: "0.5rem", color: "green" }}>✓</span>}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  const renderContentControls = () => {
    if (!state.contentControls) return null;

    return (
      <div style={{ marginBottom: "1.5rem" }}>
        <Separator alignContent="start">
          <div style={{ fontWeight: 600, fontSize: 16 }}>
            {formatMessageDefault("cof.troubleshooting.documentanalyzer.contentcontrols.title", "Content Controls")} (
            {state.contentControls.length})
          </div>
        </Separator>

        {state.contentControls.length === 0 ? (
          <MessageBar messageBarType={MessageBarType.warning} styles={{ root: { marginTop: "0.5rem" } }}>
            {formatMessageDefault(
              "cof.troubleshooting.documentanalyzer.contentcontrols.none",
              "No Content Controls found in the document."
            )}
          </MessageBar>
        ) : (
          <div style={{ marginTop: "0.5rem" }}>
            <DetailsList
              items={state.contentControls}
              columns={contentControlColumns}
              selectionMode={SelectionMode.none}
              layoutMode={DetailsListLayoutMode.justified}
              isHeaderVisible
              styles={{
                root: {
                  border: "1px solid var(--color-lightblue)",
                  borderRadius: 4,
                },
              }}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ padding: "1rem" }}>
      <div style={{ marginBottom: "1rem" }}>
        <p style={{ margin: "0.5rem 0", color: "var(--color-black)" }}>
          {formatMessageDefault(
            "cof.troubleshooting.documentanalyzer.description",
            "This tool analyzes document properties, custom XML namespaces, and content controls. Use the Export button to save detailed information including formatted XML and JSON."
          )}
        </p>
      </div>

      <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1.5rem" }}>
        <PrimaryButton
          text={formatMessageDefault("cof.troubleshooting.documentanalyzer.button.analyze", "Analyze Document")}
          onClick={analyzeContentControls}
          disabled={state.isLoading}
          iconProps={{ iconName: "Search" }}
        />

        {hasResults && (
          <DefaultButton
            text={formatMessageDefault("cof.troubleshooting.documentanalyzer.button.export", "Export")}
            onClick={exportToTxt}
            disabled={state.isLoading}
            iconProps={{ iconName: "Download" }}
          />
        )}
      </div>

      {state.isLoading && (
        <div style={{ padding: "2rem 0", textAlign: "center" }}>
          <Spinner
            size={SpinnerSize.large}
            label={formatMessageDefault(
              "cof.troubleshooting.documentanalyzer.spinner.analyzing",
              "Analyzing document..."
            )}
          />
        </div>
      )}

      {state.error && (
        <MessageBar messageBarType={MessageBarType.error} isMultiline>
          <strong>{formatMessageDefault("cof.troubleshooting.documentanalyzer.error.prefix", "Error:")}</strong>{" "}
          {state.error}
        </MessageBar>
      )}

      {!state.isLoading && !state.error && hasResults && (
        <div>
          {renderDocumentProperties()}
          {renderCustomXmlNamespaces()}
          {renderContentControls()}
        </div>
      )}
    </div>
  );
};

// Manage source differently based on its structure
function getSourceLabel(source?: unknown): string {
  if (!source) return "-";
  if (typeof source === "object") {
    const src = source as Record<string, unknown>;
    if (typeof src.type === "string" && src.type) return src.type;
    if (typeof src.objectSource === "string" && src.objectSource) return src.objectSource;
    return "-";
  }
  if (typeof source === "string") return source;
  return "-";
}
